import CloseIcon from '../../assets/icons/close.svg?react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { toast } from 'react-toastify';

import { LegalApi, PeopleApi } from '../../api';
import { Modal } from '../molecules/Modal';

type LegalAgreementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Вызывается после успешного agree (до закрытия). */
  onAccepted?: () => void;
};

/**
 * Soft-модалка согласия: можно закрыть и читать ЛК.
 * Мутации блокируются отдельно через useLegalMutationsAllowed.
 */
export const LegalAgreementModal: FC<LegalAgreementModalProps> = ({
  isOpen,
  onClose,
  onAccepted,
}) => {
  const queryClient = useQueryClient();

  const {
    data: privacy,
    isLoading: isPrivacyLoading,
    isError: isPrivacyError,
  } = useQuery({
    queryKey: [LegalApi.KEY, 'privacy'],
    queryFn: () => LegalApi.getPrivacy(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { mutate: agree, isLoading: isAgreeing } = useMutation({
    mutationFn: () => LegalApi.agree(privacy?.version),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [PeopleApi.KEY, 'me'] });
      onAccepted?.();
      onClose();
    },
    onError() {
      toast.error('Не удалось сохранить согласие. Попробуйте ещё раз');
    },
  });

  const canAgree = Boolean(privacy) && !isPrivacyLoading && !isAgreeing;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="bg-black/20"
      showCloseButton={false}
      panelClassName="max-h-[min(100vh,860px)] !max-w-[468px] w-full !rounded-3xl shadow-none md:max-h-[calc(100vh-40px)]"
    >
      <div className="flex flex-col gap-6 rounded-3xl bg-white p-6">
        <div className="flex w-full items-center gap-8">
          <h2 className="flex-1 text-2xl font-medium leading-8 text-[#151515]">
            Согласие на обработку персональных данных
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-6 shrink-0 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
            aria-label="Закрыть"
          >
            <CloseIcon className="size-6" />
          </button>
        </div>

        <p className="text-base font-normal leading-6 text-[#151515]">
          Чтобы продолжить работу с сервисом, подтвердите согласие с политикой
          обработки персональных данных.
        </p>

        {isPrivacyError ? (
          <p className="text-red-600 text-sm leading-5">
            Не удалось загрузить документ. Закройте окно и попробуйте позже.
          </p>
        ) : privacy?.file_url ? (
          <a
            href={privacy.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base font-bold leading-5 text-[#0788f1] underline-offset-2 hover:underline"
          >
            Политика обработки ПДн
          </a>
        ) : (
          <p className="text-sm leading-5 text-[#151515]/60">
            {isPrivacyLoading ? 'Загрузка документа…' : null}
          </p>
        )}

        <button
          type="button"
          disabled={!canAgree}
          onClick={() => agree()}
          className="flex h-[60px] w-full items-center justify-center rounded-xl bg-[#0788f1] px-5 text-center text-base font-bold leading-5 text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {isAgreeing ? 'Сохранение…' : 'Согласиться'}
        </button>
      </div>
    </Modal>
  );
};
